'use client'

import Link from 'next/link'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  calcularFaturamento,
  type FaturamentoModo,
  type FaturamentoStatus,
} from '@/lib/calculos/faturamento'
import { buildFaturamentoPendingCalc } from '@/lib/calculos/map-faturamento-save'
import { ANOS_LIMITE_MEI, LIMITE_MEI_ANO_REFERENCIA, getLimiteMei } from '@/lib/calculos/constantes'
import {
  AvisoBox,
  brl,
  DetailRow,
  LeadCapture,
  SalvarCalculoCta,
  Eyebrow,
  Icon,
  type ItemLista,
  ListaItens,
  MobileResultBar,
  MoneyField,
  parseValor,
  ResultCard,
  RowStrong,
  SoftExportBar,
  useAnimatedNumber,
  useCalcTelemetry,
} from '@/components/calc'
import type { CartaoPayload } from '@/lib/export/types'
import { cn } from '@/lib/utils'

const MESES_NOMES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
]

const barColor: Record<FaturamentoStatus, string> = {
  tranquilo: 'bg-brand',
  atencao: 'bg-amber',
  estourou: 'bg-neg',
}

const ANO_MAX = ANOS_LIMITE_MEI[ANOS_LIMITE_MEI.length - 1]
const ANO_PADRAO = Math.min(Math.max(LIMITE_MEI_ANO_REFERENCIA, ANOS_LIMITE_MEI[0]), ANO_MAX)

export function FaturamentoClient() {
  const [modo, setModo] = useState<FaturamentoModo>('projecao')
  const [ano, setAno] = useState<number>(ANO_PADRAO)
  const [faturamentoMes, setFaturamentoMes] = useState('')
  const [meses, setMeses] = useState<ItemLista[]>([{ id: 1, desc: '', valor: '' }])
  const nextId = useRef(2)

  // Pré-preenche com o faturamento vindo do console da home (?fat=) e o ano (?ano=2027) — sem redigitar.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const fat = params.get('fat')
    if (fat) {
      setModo('projecao')
      setFaturamentoMes(fat)
    }
    const anoParam = Number(params.get('ano'))
    if ((ANOS_LIMITE_MEI as readonly number[]).includes(anoParam)) setAno(anoParam)
  }, [])

  const addMes = (desc = '') => {
    const usados = meses.map((m) => m.desc)
    const proximo = desc || MESES_NOMES.find((m) => !usados.includes(m)) || ''
    setMeses((l) => [...l, { id: nextId.current++, desc: proximo, valor: '' }])
  }
  const removeMes = (id: number) => setMeses((l) => l.filter((m) => m.id !== id))
  const updateMes = (id: number, field: 'desc' | 'valor', val: string) =>
    setMeses((l) => l.map((m) => (m.id === id ? { ...m, [field]: val } : m)))

  const resultado = useMemo(() => {
    if (modo === 'projecao') {
      return calcularFaturamento({ modo, ano, faturamentoMes: parseValor(faturamentoMes) })
    }
    return calcularFaturamento({
      modo,
      ano,
      meses: meses.map((m) => ({ mes: m.desc, valor: parseValor(m.valor) })),
    })
  }, [modo, ano, faturamentoMes, meses])

  // Quem estoura o teto de agora quer saber: "e no teto novo, eu caibo?"
  const proximoAno = ano + 1
  const cabeNoProximo =
    ano < ANO_MAX && (resultado.projecaoFim ?? resultado.valorAno) <= getLimiteMei(proximoAno)

  const hasInput =
    modo === 'projecao'
      ? parseValor(faturamentoMes) > 0
      : meses.some((m) => parseValor(m.valor) > 0)

  const heroAnim = useAnimatedNumber(hasInput ? resultado.valorAno : 0)
  const barWidth = hasInput ? Math.min(100, Math.max(0, resultado.percentUsado)) : 0

  useCalcTelemetry(
    'faturamento',
    hasInput,
    `${ano}: ${brl(resultado.valorAno)} · ${Math.round(resultado.percentUsado)}% do teto`,
  )

  const calcData = useMemo(() => {
    if (!hasInput) return undefined
    return buildFaturamentoPendingCalc({
      modo,
      faturamentoMes: parseValor(faturamentoMes),
      meses: meses.map((m) => ({ mes: m.desc, valor: parseValor(m.valor) })),
      resultado,
    })
  }, [hasInput, modo, faturamentoMes, meses, resultado])

  const buildCartao = (): CartaoPayload => ({
    nome: 'limite-faturamento-mei',
    heroLabel: modo === 'projecao' ? 'Você vai faturar cerca de' : 'Você já faturou',
    heroValue: brl(resultado.valorAno),
    heroSuffix: modo === 'projecao' ? '/ano' : undefined,
    tone: resultado.status === 'estourou' ? 'neg' : 'brand',
    linhas:
      modo === 'projecao'
        ? [
            { label: 'Sua média por mês', value: brl(parseValor(faturamentoMes)) },
            { label: `Teto do MEI (${ano})`, value: brl(resultado.limiteAno) },
            {
              label: resultado.restanteAno >= 0 ? 'Ainda cabe no ano' : 'Passou do teto em',
              value: brl(Math.abs(resultado.restanteAno)),
              strong: true,
              tone: resultado.restanteAno >= 0 ? 'brand' : 'neg',
            },
          ]
        : [
            ...(resultado.mesesPreenchidos
              ? [
                  {
                    label: `Média de ${resultado.mesesPreenchidos} ${resultado.mesesPreenchidos === 1 ? 'mês' : 'meses'}`,
                    value: brl(resultado.mediaMensal ?? 0),
                  },
                ]
              : []),
            { label: 'Projeção pro fim do ano', value: brl(resultado.projecaoFim ?? 0) },
            { label: `Teto do MEI (${ano})`, value: brl(resultado.limiteAno) },
            {
              label: resultado.restanteAno >= 0 ? 'Pode faturar mais' : 'Passou do teto em',
              value: brl(Math.abs(resultado.restanteAno)),
              strong: true,
              tone: resultado.restanteAno >= 0 ? 'brand' : 'neg',
            },
          ],
  })

  const cardRef = useRef<HTMLElement>(null)

  return (
    <div className="grid grid-cols-1 gap-[18px] pb-24 lg:grid-cols-2 lg:items-start lg:pb-0">
      {/* ── INPUTS ── */}
      <section className="rounded-2xl border border-line bg-surface p-6 shadow-soft">
        <fieldset className="mb-5">
          <legend className="mb-2 text-[13px] font-semibold text-ink">Qual ano você quer simular?</legend>
          <div className="inline-flex flex-wrap border border-line">
            {ANOS_LIMITE_MEI.map((a) => (
              <ModoTab key={a} active={ano === a} onClick={() => setAno(a)}>
                {a} · {brl(getLimiteMei(a)).replace(/,00$/, '')}
              </ModoTab>
            ))}
          </div>
          {ano > 2026 && (
            <p className="mt-2 text-[12px] leading-snug text-ink-mute">
              Teto novo da Lei Complementar 238/2026 (Prospera MEI).{' '}
              <Link href="/prospera-mei" className="font-semibold text-brand hover:underline">
                Entenda o que muda →
              </Link>
            </p>
          )}
        </fieldset>

        <div className="mb-5 inline-flex border border-line">
          <ModoTab active={modo === 'projecao'} onClick={() => setModo('projecao')}>
            Estimar o ano
          </ModoTab>
          <ModoTab active={modo === 'controle'} onClick={() => setModo('controle')}>
            Acompanhar mês a mês
          </ModoTab>
        </div>

        {modo === 'projecao' ? (
          <MoneyField
            label="Quanto você fatura por mês, em média?"
            value={faturamentoMes}
            onChange={setFaturamentoMes}
            hint="Digite sua média — a gente multiplica por 12 pra estimar o seu ano."
            autoFocus
          />
        ) : (
          <ListaItens
            label="Quanto você faturou em cada mês?"
            items={meses}
            onAdd={addMes}
            onRemove={removeMes}
            onUpdate={updateMes}
            descPlaceholder="Ex.: Janeiro"
            addLabel="Adicionar mês"
            hint="Preencha só os meses que já fecharam. A gente projeta o resto pela sua média."
          />
        )}
      </section>

      {/* ── RESULTADO ── */}
      <section ref={cardRef} className="flex flex-col rounded-2xl border border-line bg-bg-alt p-[22px] shadow-soft">
        <Eyebrow icon="trend" tone="muted" className="mb-3">
          {modo === 'projecao' ? 'Sua projeção do ano' : 'Seu ano até agora'}
        </Eyebrow>

        {!hasInput ? (
          <div className="flex flex-1 flex-col items-center justify-center rounded-xl border border-dashed border-line bg-surface px-6 py-12 text-center">
            <Icon name="trend" size={28} className="mb-3 text-brand" />
            <p className="text-[16px] font-bold text-ink">Digite sua média mensal</p>
            <p className="mt-1.5 max-w-[32ch] text-[13.5px] leading-relaxed text-ink-mute">
              O teto do MEI em {ano} é {brl(resultado.limiteAno)}. Em segundos você vê se está
              tranquilo ou perto de estourar.
            </p>
          </div>
        ) : (
          <>
            <ResultCard
              tone={resultado.status === 'estourou' ? 'neg' : 'brand'}
              heroLabel={
                modo === 'projecao' ? 'Você vai faturar cerca de' : 'Você já faturou'
              }
              heroValue={brl(heroAnim)}
              heroSuffix={modo === 'projecao' ? '/ano' : undefined}
            >
              <div className="mb-4">
                <div className="mb-1.5 flex items-baseline justify-between">
                  <span className="text-[12px] text-ink-mute">do teto de {brl(resultado.limiteAno)}</span>
                  <span className="font-mono text-[12px] text-ink tabular-nums">
                    {resultado.percentUsado.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}%
                  </span>
                </div>
                <div className="h-2 w-full bg-line-soft">
                  <div
                    className={cn('h-full transition-all', barColor[resultado.status])}
                    style={{ width: `${barWidth}%` }}
                  />
                </div>
              </div>

              {modo === 'projecao' ? (
                <>
                  <DetailRow label="Sua média por mês" value={brl(parseValor(faturamentoMes))} />
                  <DetailRow label={`Teto do MEI (${ano})`} value={brl(resultado.limiteAno)} />
                  <RowStrong
                    label={resultado.restanteAno >= 0 ? 'Ainda cabe no ano' : 'Passou do teto em'}
                    value={brl(Math.abs(resultado.restanteAno))}
                    tone={resultado.restanteAno >= 0 ? 'brand' : 'neg'}
                  />
                </>
              ) : (
                <>
                  {resultado.mesesPreenchidos ? (
                    <DetailRow
                      label={`Média de ${resultado.mesesPreenchidos} ${resultado.mesesPreenchidos === 1 ? 'mês' : 'meses'}`}
                      value={brl(resultado.mediaMensal ?? 0)}
                    />
                  ) : null}
                  <DetailRow label="Projeção pro fim do ano" value={brl(resultado.projecaoFim ?? 0)} />
                  <DetailRow label={`Teto do MEI (${ano})`} value={brl(resultado.limiteAno)} />
                  <RowStrong
                    label={resultado.restanteAno >= 0 ? 'Pode faturar mais' : 'Passou do teto em'}
                    value={brl(Math.abs(resultado.restanteAno))}
                    tone={resultado.restanteAno >= 0 ? 'brand' : 'neg'}
                  />
                </>
              )}
            </ResultCard>

            <SoftExportBar buildCartao={buildCartao} />
            <LeadCapture show={hasInput} source="limite-faturamento-mei" alertType="das" />
            <SalvarCalculoCta
              show={hasInput}
              source="limite-faturamento-mei"
              hook="Esse número é só o começo — veja o teto e o mês no Premium."
              calcData={calcData}
            />


            {resultado.status === 'atencao' && (
              <AvisoBox tone="amber" className="mt-4">
                Você está chegando perto do teto. Continue de olho nos próximos meses pra não
                passar de {brl(resultado.limiteAno)} no ano.
              </AvisoBox>
            )}
            {resultado.status === 'estourou' && resultado.faixaExcesso !== 'acima20' && (
              <AvisoBox tone="neg" icon="alert" className="mt-4">
                Nesse ritmo você passa do teto de {brl(resultado.limiteAno)}, mas em até 20% (até{' '}
                {brl(resultado.limiteTolerancia)}). Pela regra atual, você paga um DAS complementar
                sobre o excesso e vira Microempresa (ME) a partir de janeiro de {ano + 1}.
              </AvisoBox>
            )}
            {resultado.faixaExcesso === 'acima20' && (
              <AvisoBox tone="neg" icon="alert" className="mt-4">
                Nesse ritmo você passa mais de 20% do teto (acima de{' '}
                {brl(resultado.limiteTolerancia)}). Aí o desenquadramento é retroativo a janeiro de{' '}
                {ano}, com impostos de Microempresa sobre o ano todo. Converse com um contador antes
                de chegar lá.
              </AvisoBox>
            )}
            {resultado.status !== 'tranquilo' && cabeNoProximo && (
              <AvisoBox tone="amber" className="mt-4">
                Em {proximoAno} o teto sobe para {brl(getLimiteMei(proximoAno))} — no seu ritmo, você
                caberia nele.{' '}
                <button
                  type="button"
                  onClick={() => setAno(proximoAno)}
                  className="font-semibold text-brand underline-offset-2 hover:underline"
                >
                  Simular {proximoAno} →
                </button>
                {ano === 2026 && (
                  <>
                    {' '}Mas o teto de 2026 continua {brl(resultado.limiteAno)} e a lei não trouxe regra
                    de transição.{' '}
                    <Link
                      href="/blog/passei-limite-mei-2026-o-que-fazer"
                      className="font-semibold text-brand hover:underline"
                    >
                      Veja o que fazer
                    </Link>
                    .
                  </>
                )}
              </AvisoBox>
            )}

          </>
        )}
      </section>

      {hasInput && <MobileResultBar payload={buildCartao()} cardRef={cardRef} />}
    </div>
  )
}

function ModoTab({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center gap-1.5 px-3.5 py-2 text-[13px] font-medium',
        active ? 'bg-brand text-white' : 'bg-surface text-ink-mute hover:text-ink'
      )}
    >
      {active && <Icon name="check" size={13} />}
      {children}
    </button>
  )
}
